export type TerminalStreamEvent = {
  type: string;
  text?: string;
  reason?: string;
  [key: string]: unknown;
};

export * from "./observer-projection.ts";

export interface TerminalObserverLike {
  close(): void;
}

export type TerminalStreamPhase = "idle" | "connecting" | "connected" | "closed";

export interface TerminalStreamState {
  phase: TerminalStreamPhase;
  error: string | null;
  reconnectAttempt: number;
}

export interface TerminalStreamRuntimeOptions {
  open: (onEvent: (event: TerminalStreamEvent) => void) => Promise<TerminalObserverLike>;
  maxReconnectAttempts?: number;
  reconnectDelay?: (attempt: number) => number;
  stableConnectionMs?: number;
  formatError?: (error: unknown) => string;
  formatEventError?: (event: TerminalStreamEvent) => string;
  retryingMessage?: string;
  retryLimitMessage?: string;
}

type StateListener = (state: TerminalStreamState) => void;
type EventListener = (event: TerminalStreamEvent) => void;

const defaultFormatError = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Shared lifecycle policy for observe-only terminal streams.
 *
 * The runtime deliberately knows nothing about WebSocket, Cloud or PTY
 * frames. An adapter supplies an observer factory; callers receive the same
 * parsed events and a deterministic connection state on every platform.
 */
export class TerminalStreamRuntime {
  private readonly options: Required<Pick<
    TerminalStreamRuntimeOptions,
    "maxReconnectAttempts" | "reconnectDelay" | "stableConnectionMs" | "formatError"
  >> & TerminalStreamRuntimeOptions;
  private readonly stateListeners = new Set<StateListener>();
  private readonly eventListeners = new Set<EventListener>();
  private stateValue: TerminalStreamState = {
    phase: "idle",
    error: null,
    reconnectAttempt: 0,
  };
  private observer: TerminalObserverLike | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionToken = 0;
  private connecting = false;
  private started = false;
  private online = true;
  private visible = true;
  private disposed = false;

  constructor(options: TerminalStreamRuntimeOptions) {
    this.options = {
      ...options,
      maxReconnectAttempts: Math.max(0, options.maxReconnectAttempts ?? 6),
      reconnectDelay: options.reconnectDelay ?? ((attempt) => Math.min(30_000, 1_000 * 2 ** Math.max(0, attempt - 1))),
      stableConnectionMs: Math.max(0, options.stableConnectionMs ?? 30_000),
      formatError: options.formatError ?? defaultFormatError,
    };
  }

  get state(): TerminalStreamState {
    return this.stateValue;
  }

  subscribe(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.stateValue);
    return () => this.stateListeners.delete(listener);
  }

  subscribeEvents(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  start(): void {
    if (this.disposed) return;
    this.started = true;
    this.connectIfReady();
  }

  setOnline(online: boolean): void {
    if (this.disposed || this.online === online) return;
    this.online = online;
    if (!online) {
      this.closeObserver();
      this.clearTimers();
      this.setState({ phase: "closed", error: "offline" });
      return;
    }
    this.setState({ error: null, reconnectAttempt: 0 });
    this.connectIfReady();
  }

  setVisible(visible: boolean): void {
    if (this.disposed || this.visible === visible) return;
    this.visible = visible;
    if (!visible) {
      this.closeObserver();
      this.clearTimers();
      this.setState({ phase: "idle", error: null });
      return;
    }
    this.setState({ error: null, reconnectAttempt: 0 });
    this.connectIfReady();
  }

  retry(): void {
    if (this.disposed) return;
    this.clearTimers();
    this.closeObserver();
    this.setState({ reconnectAttempt: 0, error: null });
    this.connectIfReady();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.started = false;
    this.connectionToken += 1;
    this.clearTimers();
    this.closeObserver();
    this.stateListeners.clear();
    this.eventListeners.clear();
  }

  private connectIfReady(): void {
    if (!this.started || !this.online || !this.visible || this.disposed || this.connecting || this.observer) return;
    this.clearRetryTimer();
    this.connecting = true;
    this.setState({ phase: "connecting" });
    const token = ++this.connectionToken;
    const onEvent = (event: TerminalStreamEvent) => this.handleEvent(token, event);
    void this.options.open(onEvent).then((observer) => {
      if (this.disposed || token !== this.connectionToken || !this.started || !this.online || !this.visible) {
        observer.close();
        return;
      }
      this.observer = observer;
      this.connecting = false;
    }).catch((error) => {
      if (token !== this.connectionToken || this.disposed) return;
      this.connecting = false;
      this.handleFailure(this.options.formatError(error));
    });
  }

  private handleEvent(token: number, event: TerminalStreamEvent): void {
    if (this.disposed || token !== this.connectionToken) return;
    for (const listener of this.eventListeners) listener(event);
    if (event.type === "joined") {
      this.setState({ phase: "connected", error: null });
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        if (!this.disposed && token === this.connectionToken) this.setState({ reconnectAttempt: 0 });
      }, this.options.stableConnectionMs);
      return;
    }
    if (event.type === "gap" || event.type === "closed") {
      this.clearStableTimer();
      this.closeObserver();
      this.handleFailure(
        this.options.formatEventError?.(event)
          ?? event.reason
          ?? (event.type === "gap" ? "terminal stream gap" : "terminal stream closed"),
        event.type === "gap",
      );
    }
  }

  private handleFailure(reason: string, protocolGap = false): void {
    if (this.disposed || !this.started || !this.online || !this.visible) return;
    const nextAttempt = this.stateValue.reconnectAttempt + 1;
    if (nextAttempt > this.options.maxReconnectAttempts) {
      this.setState({ phase: "closed", error: this.options.retryLimitMessage ?? reason, reconnectAttempt: nextAttempt });
      return;
    }
    this.setState({
      phase: "connecting",
      error: protocolGap ? reason : (this.options.retryingMessage ?? reason),
      reconnectAttempt: nextAttempt,
    });
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connectIfReady();
    }, Math.max(0, this.options.reconnectDelay(nextAttempt)));
  }

  private setState(patch: Partial<TerminalStreamState>): void {
    this.stateValue = { ...this.stateValue, ...patch };
    for (const listener of this.stateListeners) listener(this.stateValue);
  }

  private closeObserver(): void {
    this.connectionToken += 1;
    this.connecting = false;
    const observer = this.observer;
    this.observer = null;
    observer?.close();
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) clearTimeout(this.stableTimer);
    this.stableTimer = null;
  }

  private clearTimers(): void {
    this.clearRetryTimer();
    this.clearStableTimer();
  }
}

export interface TerminalConnectionLike {
  close(): void;
  onClose(listener: () => void): () => void;
}

export interface TerminalConnectionRuntimeOptions<TConnection extends TerminalConnectionLike> {
  open: () => Promise<TConnection>;
  shouldReconnect?: (reason: string) => Promise<boolean> | boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: (attempt: number) => number;
  stableConnectionMs?: number;
  formatError?: (error: unknown) => string;
  retryingMessage?: string;
  retryLimitMessage?: string;
}

/**
 * Shared lifecycle policy for platform terminal connection objects.
 *
 * The connection itself remains platform-owned (xterm, control leases and
 * protocol events stay outside this package). This class only guarantees one
 * active connection, fenced close callbacks and bounded reconnect behavior.
 */
export class TerminalConnectionRuntime<TConnection extends TerminalConnectionLike> {
  private readonly options: Required<Pick<
    TerminalConnectionRuntimeOptions<TConnection>,
    "maxReconnectAttempts" | "reconnectDelay" | "stableConnectionMs" | "formatError"
  >> & TerminalConnectionRuntimeOptions<TConnection>;
  private readonly stateListeners = new Set<StateListener>();
  private readonly connectionListeners = new Set<(connection: TConnection) => void>();
  private stateValue: TerminalStreamState = { phase: "idle", error: null, reconnectAttempt: 0 };
  private connection: TConnection | null = null;
  private unsubscribeClose: (() => void) | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionToken = 0;
  private connecting = false;
  private started = false;
  private online = true;
  private visible = true;
  private disposed = false;

  constructor(options: TerminalConnectionRuntimeOptions<TConnection>) {
    this.options = {
      ...options,
      maxReconnectAttempts: Math.max(0, options.maxReconnectAttempts ?? 8),
      reconnectDelay: options.reconnectDelay ?? ((attempt) => Math.min(8_000, 500 * 2 ** Math.max(0, attempt - 1))),
      stableConnectionMs: Math.max(0, options.stableConnectionMs ?? 10_000),
      formatError: options.formatError ?? defaultFormatError,
    };
  }

  get state(): TerminalStreamState { return this.stateValue; }
  get current(): TConnection | null { return this.connection; }

  subscribe(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.stateValue);
    return () => this.stateListeners.delete(listener);
  }

  subscribeConnections(listener: (connection: TConnection) => void): () => void {
    this.connectionListeners.add(listener);
    if (this.connection) listener(this.connection);
    return () => this.connectionListeners.delete(listener);
  }

  start(): void {
    if (this.disposed) return;
    this.started = true;
    this.connectIfReady();
  }

  setOnline(online: boolean): void {
    if (this.disposed || this.online === online) return;
    this.online = online;
    if (!online) {
      this.closeConnection();
      this.clearTimers();
      this.setState({ phase: "closed", error: "offline" });
      return;
    }
    this.setState({ error: null, reconnectAttempt: 0 });
    this.connectIfReady();
  }

  setVisible(visible: boolean): void {
    if (this.disposed || this.visible === visible) return;
    this.visible = visible;
    if (!visible) {
      this.closeConnection();
      this.clearTimers();
      this.setState({ phase: "idle", error: null });
      return;
    }
    this.setState({ error: null, reconnectAttempt: 0 });
    this.connectIfReady();
  }

  retry(): void {
    if (this.disposed) return;
    this.clearTimers();
    this.closeConnection();
    this.setState({ reconnectAttempt: 0, error: null });
    this.connectIfReady();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.started = false;
    this.connectionToken += 1;
    this.clearTimers();
    this.closeConnection();
    this.stateListeners.clear();
    this.connectionListeners.clear();
  }

  private connectIfReady(): void {
    if (!this.started || !this.online || !this.visible || this.disposed || this.connecting || this.connection) return;
    this.clearRetryTimer();
    this.connecting = true;
    this.setState({ phase: "connecting" });
    const token = ++this.connectionToken;
    void this.options.open().then((connection) => {
      if (this.disposed || token !== this.connectionToken || !this.started || !this.online || !this.visible) {
        connection.close();
        return;
      }
      this.connection = connection;
      this.connecting = false;
      this.unsubscribeClose = connection.onClose(() => {
        if (this.disposed || token !== this.connectionToken || this.connection !== connection) return;
        this.connection = null;
        this.unsubscribeClose?.();
        this.unsubscribeClose = null;
        this.clearStableTimer();
        void this.handleFailure("terminal connection closed", token);
      });
      this.setState({ phase: "connected", error: null });
      for (const listener of this.connectionListeners) listener(connection);
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        if (!this.disposed && token === this.connectionToken) this.setState({ reconnectAttempt: 0 });
      }, this.options.stableConnectionMs);
    }).catch((error) => {
      if (token !== this.connectionToken || this.disposed) return;
      this.connecting = false;
      void this.handleFailure(this.options.formatError(error), token);
    });
  }

  private async handleFailure(reason: string, token: number): Promise<void> {
    if (this.disposed || !this.started || !this.online || !this.visible || token !== this.connectionToken) return;
    const shouldReconnect = await this.options.shouldReconnect?.(reason);
    if (this.disposed || token !== this.connectionToken) return;
    if (shouldReconnect === false) {
      this.setState({ phase: "closed", error: null });
      return;
    }
    const nextAttempt = this.stateValue.reconnectAttempt + 1;
    if (nextAttempt > this.options.maxReconnectAttempts) {
      this.setState({ phase: "closed", error: this.options.retryLimitMessage ?? reason, reconnectAttempt: nextAttempt });
      return;
    }
    this.setState({
      phase: "connecting",
      error: this.options.retryingMessage ?? reason,
      reconnectAttempt: nextAttempt,
    });
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connectIfReady();
    }, Math.max(0, this.options.reconnectDelay(nextAttempt)));
  }

  private setState(patch: Partial<TerminalStreamState>): void {
    this.stateValue = { ...this.stateValue, ...patch };
    for (const listener of this.stateListeners) listener(this.stateValue);
  }

  private closeConnection(): void {
    this.connectionToken += 1;
    this.connecting = false;
    this.unsubscribeClose?.();
    this.unsubscribeClose = null;
    const connection = this.connection;
    this.connection = null;
    connection?.close();
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) clearTimeout(this.stableTimer);
    this.stableTimer = null;
  }

  private clearTimers(): void {
    this.clearRetryTimer();
    this.clearStableTimer();
  }
}
